import { Logger } from '@mobalytics/shared';
import * as React from 'react';
import type { FetchResult } from '@apollo/client';
import { TreasuryPaymentsProvider } from '../../../../__generated/treasury/types';
import { treasuryApolloClient } from '../../../../treasury-apollo-client';
import {
  PaymentV2CheckoutMutation,
  usePaymentV2CheckoutMutation,
} from '../../_gql/__generated/payment-v2-checkout.mutation.gql.generated';

type ReturnT = (
  | {
      status: 'loading';
    }
  | {
      status: 'error';
      errorMessage: string;
    }
  | {
      status: 'success';
      clientSecret: string;
    }
) & {
  checkout: (
    oneTimeItems: { priceId: string }[],
    subscriptionItem: { planId: string; coupon: string | null } | undefined
  ) => Promise<FetchResult<PaymentV2CheckoutMutation>>;
};

export const usePaymentFlowCheckout = (provider: TreasuryPaymentsProvider): ReturnT => {
  const [checkoutMutation, { error, data, loading, called }] = usePaymentV2CheckoutMutation({
    client: treasuryApolloClient,
    errorPolicy: 'all',
  });

  const checkout = React.useCallback(
    async (
      oneTimeItems: { priceId: string }[],
      subscriptionItem: { planId: string; coupon: string | null } | undefined
    ): Promise<FetchResult<PaymentV2CheckoutMutation>> => {
      const response = await checkoutMutation({
        variables: {
          input: {
            provider: provider,
            oneTimeItems: oneTimeItems,
            subscriptionItem: subscriptionItem,
          },
        },
      });
      return response;
    },
    [checkoutMutation, provider]
  );

  if (loading || !called) {
    return {
      status: 'loading',
      checkout,
    };
  }
  const checkoutError = data?.treasury?.payments?.checkout?.error || error;

  if (checkoutError) {
    Logger.error('[usePaymentFlow]:[checkout]: error', checkoutError);
    return {
      status: 'error',
      errorMessage: 'Internal server error',
      checkout,
    };
  }

  const clientSecret = data?.treasury?.payments?.checkout?.clientSecret;
  if (!clientSecret) {
    return {
      status: 'error',
      errorMessage: 'Internal server error',
      checkout,
    };
  }

  return {
    status: 'success',
    clientSecret,
    checkout,
  };
};
